/// <reference types="google.maps" />
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { APIProvider, Map as GoogleMap, useMap } from '@vis.gl/react-google-maps'
import { CLARA_VALE_MAP_STYLES, MAPS_KEY } from '@/shared/components/maps/map-config'
import { useRegionOutlines, type RegionCollection } from './heat-map-geo'
import { shadeStep } from './heat-map-scale'

/**
 * The heat map on a real Google map (WPS-232 #6): the country's regions
 * (Natural Earth outlines, any country) shaded by count on Google's base map.
 * Hover shows the name and count; click opens the records. The caller only
 * renders this when a Maps key is configured - otherwise the outline map.
 */

export interface RegionCount {
  label: string
  value: number
}

// Opacity per shade step - the fill is the theme accent, read at runtime,
// because the Maps canvas sits outside the stylesheet.
const STEP_OPACITY = [0.14, 0.3, 0.5, 0.72, 0.92]

function accent(): string {
  if (typeof window === 'undefined') return '#1c5a48'
  const v = getComputedStyle(document.documentElement).getPropertyValue('--green').trim()
  return v || '#1c5a48'
}

function Layers({
  outlines,
  counts,
  max,
  onOpen,
  onHover,
}: {
  outlines: RegionCollection
  counts: Map<string, RegionCount>
  max: number
  onOpen: (code: string) => void
  onHover: (code: string | null) => void
}) {
  const map = useMap()

  useEffect(() => {
    if (!map) return
    const added = map.data.addGeoJson(outlines)
    const fill = accent()
    const codeOf = (f: google.maps.Data.Feature) => String(f.getProperty('code') ?? '')
    map.data.setStyle((f: google.maps.Data.Feature) => {
      const step = shadeStep(counts.get(codeOf(f))?.value ?? 0, max)
      return {
        fillColor: fill,
        fillOpacity: step < 0 ? 0 : STEP_OPACITY[step],
        strokeColor: step < 0 ? '#c9c4b8' : '#ffffff',
        strokeWeight: 0.8,
        cursor: step < 0 ? 'default' : 'pointer',
      }
    })
    // Frame the country.
    const all = new google.maps.LatLngBounds()
    for (const f of added) f.getGeometry()?.forEachLatLng((ll) => all.extend(ll))
    if (!all.isEmpty()) map.fitBounds(all, 16)

    const over = map.data.addListener('mouseover', (e: google.maps.Data.MouseEvent) => {
      onHover(codeOf(e.feature) || null)
      map.data.overrideStyle(e.feature, { strokeWeight: 2, strokeColor: '#1a1a18' })
    })
    const out = map.data.addListener('mouseout', (e: google.maps.Data.MouseEvent) => {
      map.data.revertStyle(e.feature)
      onHover(null)
    })
    const click = map.data.addListener('click', (e: google.maps.Data.MouseEvent) => {
      const code = codeOf(e.feature)
      if ((counts.get(code)?.value ?? 0) > 0) onOpen(code)
    })
    // The number on every region that has records.
    const labels: google.maps.Marker[] = []
    for (const f of added) {
      const value = counts.get(codeOf(f))?.value ?? 0
      if (value <= 0) continue
      const bounds = new google.maps.LatLngBounds()
      f.getGeometry()?.forEachLatLng((ll) => bounds.extend(ll))
      const dark = shadeStep(value, max) >= 3
      labels.push(
        new google.maps.Marker({
          map,
          position: bounds.getCenter(),
          clickable: false,
          icon: { path: google.maps.SymbolPath.CIRCLE, scale: 0 },
          label: { text: value.toLocaleString(), color: dark ? '#ffffff' : '#1a1a18', fontSize: '12px', fontWeight: '600' },
        }),
      )
    }
    return () => {
      over.remove()
      out.remove()
      click.remove()
      labels.forEach((m) => m.setMap(null))
      added.forEach((f: google.maps.Data.Feature) => map.data.remove(f))
    }
  }, [map, outlines, counts, max, onOpen, onHover])

  return null
}

export function GoogleHeatMap({
  country,
  word,
  counts,
  max,
  unit,
  onOpen,
}: {
  country: string
  /** What the country calls its regions ("state", "province", "county"). */
  word: string
  counts: Map<string, RegionCount>
  max: number
  unit: string
  onOpen: (code: string) => void
}) {
  const { t } = useTranslation()
  const { data: outlines, isPending, isError } = useRegionOutlines(country)
  const [hover, setHover] = useState<string | null>(null)
  const hovered = hover ? counts.get(hover) : undefined

  if (isPending || isError || !outlines) {
    return (
      <p className="py-10 text-center text-[13px] text-[var(--ink-3)]">
        {isPending
          ? t('common.loading', 'Loading...')
          : t('dashboard.heatMap.noOutlines', 'No map for this country yet - the list shows its counts.')}
      </p>
    )
  }

  return (
    <div
      className="relative h-[380px] overflow-hidden rounded-[12px] border-[0.5px] border-[var(--rule)]"
      data-testid="heat-map-google"
      data-country={country}
    >
      <APIProvider apiKey={MAPS_KEY}>
        <GoogleMap
          defaultCenter={{ lat: 30, lng: 0 }}
          defaultZoom={2}
          gestureHandling="cooperative"
          disableDefaultUI
          zoomControl
          styles={CLARA_VALE_MAP_STYLES}
          className="h-full w-full"
        >
          <Layers outlines={outlines} counts={counts} max={max} onOpen={onOpen} onHover={setHover} />
        </GoogleMap>
      </APIProvider>
      <div className="pointer-events-none absolute left-3 top-3 rounded-[9px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-3 py-1.5 text-[12.5px] text-[var(--ink)] shadow-[var(--shadow-float)]">
        {hovered
          ? `${hovered.label}: ${hovered.value.toLocaleString()} ${unit}`
          : t('dashboard.heatMap.hoverHint', {
              defaultValue: 'Hover a {{word}} to see its count - click to open the records',
              word,
            })}
      </div>
    </div>
  )
}
